import { datadogRum } from '@datadog/browser-rum';
import type { RumBeforeSend, RumEvent } from '@datadog/browser-rum';

/**
 * Redação de dado pessoal em URL coletada pelo Datadog RUM (LGPD) — issue web#1071.
 *
 * `defaultPrivacyLevel: MASK_USER_INPUT` mascara o que o usuário digita no **session replay**;
 * não toca na query string das URLs que o RUM coleta em `resource.url`, `view.url` e nos erros de
 * rede. Quem digita "12345678909" na busca da Secretaria manda o CPF num `search=` — e o
 * CLAUDE.md (seção Logging) proíbe CPF/CNPJ em log, mesmo parcial.
 *
 * O `beforeSend` é o ponto de estrangulamento: redigir aqui cobre **todo** chamador de uma vez,
 * presente e futuro, em vez de caçar cada `params.set()` espalhado pelas features.
 *
 * Some só o **valor** do parâmetro. Host, caminho e demais parâmetros (`page`, `limit`, `status`)
 * continuam byte a byte como estavam, porque é deles que sai a agregação por rota no RUM.
 * O preço de preservar o caminho é a credencial que mora no path (`/portal/{hash}`,
 * `/auth/token/{token}`, `/convite/{token}`) continuar crua — tratada na issue web#1084, porque
 * exige mudar a forma da redação, não a lista.
 *
 * ## O `beforeSend` recebe uma CÓPIA, e o SDK só copia de volta os campos da allowlist dele
 *
 * `limitModification` (`@datadog/browser-rum-core/src/domain/limitModification.ts:20-28`) faz
 * `deepClone` do evento, roda este callback na cópia e devolve ao evento original **apenas** os
 * caminhos de `modifiableFieldPathsByEvent` (`.../domain/assembly.ts:45-87`). Escrever em qualquer
 * outro campo é descartado **em silêncio** — vira falsa sensação de proteção, não proteção.
 *
 * Allowlist da versão instalada (6.32.0) e o que cada campo pede aqui:
 *
 * | campo modificável                      | evento    | tratamento |
 * | -------------------------------------- | --------- | ---------- |
 * | `view.url`, `view.referrer`            | todos     | redigido |
 * | `view.name`                            | todos     | não é URL; o app nunca chama `startView` |
 * | `context`, `service`, `version`        | todos     | não são URL |
 * | `view.performance.lcp.resource_url`    | view      | redigido |
 * | `error.message`, `.stack`, `.handling_stack`, `.resource.url` | error | redigidos |
 * | `error.fingerprint`                    | error     | chave de agrupamento, não URL; o app não a define |
 * | `resource.url`                         | resource  | redigido |
 * | `resource.graphql.variables`           | resource  | o app não usa GraphQL |
 * | `action.target.name`                   | action    | rótulo do elemento clicado, não URL — outra invariante |
 * | `long_task.scripts[].source_url`, `.invoker` | long_task | redigidos |
 *
 * `error.causes[]` **não** está na allowlist: redigir as causas aqui não teria efeito nenhum no
 * evento publicado. Por isso o evento inteiro é descartado quando uma causa vazaria — ver
 * `redactRumEvent`.
 */

/** Placeholder fixo — não é hash: hash de CPF ainda é identificador estável do titular. */
const REDACTED_QUERY_VALUE = 'REDACTED';

/**
 * Parâmetros cujo valor o CLAUDE.md (seção Logging → PII) proíbe registrar: identidade, contato,
 * endereço, valor de caso, texto livre digitado pelo usuário — e credencial de sessão, que a mesma
 * seção veta nominalmente ("JWT or refresh tokens", "cookies / session identifiers").
 *
 * Entradas sempre em minúsculas: a comparação lowercases o nome que vem na URL, então uma entrada
 * em camelCase nunca casaria.
 *
 * Fora da lista de propósito: `estado` (é a UF de `/tribunais?estado=SP`, filtro legítimo e não
 * identificante), `valorMin`/`valorMax` (limites de filtro escolhidos na tela, não o valor de um
 * caso) e identificadores opacos (`id`, `processoId`, `loteId`, `workspaceId`), que são
 * justamente o que sustenta o diagnóstico em produção.
 *
 * Denylist falha **aberta**: parâmetro novo que alguém criar amanhã vaza calado até esta lista ser
 * editada — foi o que aconteceu com as credenciais abaixo, que já estavam em produção quando a
 * lista nasceu. TODO(#1086): guarda automática comparando os nomes de parâmetro usados em `src/`
 * com esta lista + uma `KNOWN_SAFE_QUERY_PARAMS`.
 *
 * `numeroProcesso` também fica fora, e aqui a decisão é menos óbvia: ele não é opaco como
 * `processoId`. Fica de fora porque identifica o **caso**, não a pessoa — nenhum nome, documento
 * ou contato sai dele —, e porque é o único correlator presente na rota que o usa
 * (`/admin-geral/chat-atendimento`), onde não há `processoId` para substituí-lo. Se a leitura do
 * time mudar para "identifica indiretamente as partes", basta acrescentar `'numeroprocesso'`.
 */
const SENSITIVE_QUERY_PARAMS = new Set([
  // Busca e texto livre — o termo digitado pode ser qualquer coisa, inclusive CPF/CNPJ.
  'search',
  'q',
  'busca',
  'termo',
  'breverelato',
  'objective',
  'title',
  // Documento.
  'cpf',
  'cnpj',
  'cpfcnpj',
  'doc',
  'documento',
  // Identidade e contato.
  'nome',
  'name',
  'email',
  'phone',
  'telefone',
  'requerente',
  'requerido',
  'solicitante',
  'responsavel',
  // Nome da entidade que o signatário declarou representar, mandado pelo filtro "Representação"
  // da visão de assinaturas (`GET /negociacao/assinaturas?representacaoIn[]=…`). O core o monta
  // com variáveis do caso e o `retention-lgpd.md` o proíbe em evento. `rotuloIn`, da mesma
  // consulta, fica de fora: é o papel no documento (Contraparte, Testemunha 1 ou a qualificação
  // do modelo da ZapSign), não a pessoa.
  'representacaoin',
  // Endereço.
  'cep',
  'logradouro',
  'numero',
  'complemento',
  'bairro',
  'cidade',
  // Valor do caso — "valores de acordo" é PII explícita no CLAUDE.md.
  'valor',
  'valorcausa',
  'claimamount',
  // Credencial em URL — o CLAUDE.md §Logging veta token e identificador de sessão junto com
  // CPF/CNPJ, e todas estas já trafegam na query hoje:
  // `?token=` (convite-arbitro, verify-email, onboarding/boas-vindas) e `?code=` (autenticar,
  // portal/[hash], auth/signup, compartilhado/login) ficam na barra de endereço, logo em
  // `view.url` de TODO evento daquela view; `?portalHash=` idem (portal/[hash]/page.tsx:55,87) e
  // é capability URL — quem tem o valor entra; `?hash=` é o mesmo valor com outro nome, no magic
  // link que o core monta (`/portal/magic/{token}?hash=…`, portalMagicLinkService.ts:71);
  // `?pollToken=` vai em `resource.url` a cada volta do
  // polling de magic link (portal/store/services.ts:253). `?key=` é a chave da API de geolocation
  // (shared/services/geolocationService.ts:18): mesmo sendo `NEXT_PUBLIC_` e já visível no bundle,
  // não há por que indexá-la no Datadog. Nenhum destes é filtro diagnosticável, então a agregação
  // por rota não perde nada.
  // `?o=` é o token de origem do envio da notificação (`/portal/{hash}?o=…`, repassado a
  // `/auth/signup`): atribui a resposta ao e-mail, telefone ou carta daquele envio, e quem o tem
  // forja a atribuição. Fica na barra até a sessão nascer, logo em `view.url` e `view.referrer`.
  'o',
  'token',
  'polltoken',
  'portalhash',
  'hash',
  'code',
  'key',
  // Assinatura de URL de storage. Estes nomes NÃO aparecem em `src/` — quem os escreve é o
  // assinador do core-api (`domains/core/shared/storage/gcsStorageProvider.ts:119,149`, GCS
  // **v4**) —, mas a URL assinada é buscada pelo browser (`shared/utils/uploadParaUrlAssinada.ts`
  // faz `xhr.open('PUT', destino.uploadUrl)`) e vira `resource.url`. Quem tem a
  // assinatura escreve no objeto até expirar.
  //
  // Os demais parâmetros da v4 (`X-Goog-Algorithm`, `X-Goog-Date`, `X-Goog-Expires`,
  // `X-Goog-SignedHeaders`) ficam **de fora de propósito**: não autorizam nada sozinhos e são
  // justamente o que permite diagnosticar "URL assinada expirada" no Datadog.
  'x-goog-signature',
  'x-goog-credential',
]);

/**
 * Um par `chave=valor`. O padrão muda com o nível, porque os dois níveis são strings de natureza
 * diferente — e tratá-los igual vaza nos dois sentidos.
 *
 * **Nível 0** é texto livre com URL embutida (`error.message`, `error.stack`), então:
 * - o par tem de vir precedido de `?` ou `&`, senão qualquer `total=12` de uma frase viraria par;
 * - o valor termina em `&`, `#` **ou espaço** — e nada mais: `)`, `"` e `'` são caracteres legais
 *   em query string, e tirá-los do terminador faria a redação parar cedo e vazar o resto. O preço
 *   é engolir a pontuação que fecha uma URL citada num stack — erro para o lado seguro.
 *
 * **Nível ≥ 1** é, por construção, o valor de um parâmetro do nível anterior — que já excluiu
 * espaço e `#`. Logo:
 * - espaço ali **pertence ao valor** (só pode ter vindo de um `%20` decodificado): mantê-lo como
 *   terminador truncaria `nome=Maria Silva` em `nome=REDACTED Silva`, vazando a cauda. `#` segue
 *   terminando, porque ali ele é o fragmento do próprio endereço aninhado — `#` dentro de um
 *   valor chega como `%23` e não é tocado;
 * - o primeiro par não tem `?`/`&` na frente quando o valor é uma query nua (`doc=1&x=2`), por
 *   isso o início da string também abre par.
 *
 * O nome exclui `?` nos dois níveis: sem isso, o começo de string casaria `/criar?name` como um
 * nome só, engolindo o par de verdade que vinha depois dele.
 *
 * Construído a cada uso, e não guardado numa constante: a redação é recursiva (query aninhada), e
 * regex global carrega `lastIndex` — reaproveitar o mesmo objeto entre os níveis é armadilha.
 */
function novoPadraoDePar(profundidade: number): RegExp {
  return profundidade === 0 ? /([?&])([^?&=#\s]+)=([^&#\s]*)/g : /(^|[?&])([^?&=#\s]+)=([^&#]*)/g;
}

/**
 * Um nível de aninhamento é o que o repo produz hoje: `Passo1ConfiguracaoEnvioIndividual.tsx:187`
 * faz `encodeURIComponent(pathname + search)` e empacota a query INTEIRA do prefill — com `doc`
 * (CPF), `name`, `email`, `phone` e endereço — dentro de `returnUrl=`. Para o padrão acima aquilo
 * é um único valor opaco, e sem esta passagem o CPF voltaria byte a byte.
 *
 * Trata a **forma**, não o nome: qualquer `redirect=`/`next=` futuro entra pelo mesmo caminho, sem
 * depender de alguém lembrar de acrescentar o nome à lista.
 */
const PROFUNDIDADE_MAXIMA = 2;

function decodeParamName(rawName: string): string {
  try {
    return decodeURIComponent(rawName.replace(/\+/g, ' '));
  } catch {
    // Percent-encoding quebrado: compara o nome cru em vez de desistir da redação.
    return rawName;
  }
}

/**
 * Filtro em array sai como `campo[]=…` repetido (`queryDaConsulta` das assinaturas, a listagem de
 * negociações): sem tirar o sufixo, a entrada `campo` da lista nunca casaria com esse nome, e o
 * valor vazaria calado. Tirar aqui cobre todo filtro em array, presente e futuro.
 */
function semSufixoDeArray(name: string): string {
  return name.endsWith('[]') ? name.slice(0, -2) : name;
}

function isSensitiveQueryParam(rawName: string): boolean {
  return SENSITIVE_QUERY_PARAMS.has(
    semSufixoDeArray(decodeParamName(rawName).trim().toLowerCase()),
  );
}

/**
 * `decodeURIComponent` do que dá para decodificar, deixando intacto o que não dá.
 *
 * `decodeURIComponent` é tudo-ou-nada: um `%ZZ` ou um `%` truncado no fim faz a string INTEIRA
 * falhar. Como esta redação também roda sobre `error.message`/`error.stack`, onde URL aparece
 * cortada no meio, aquele throw deixaria passar sem redação um `returnUrl` cujos separadores são
 * `%3F`/`%26`/`%3D` — o padrão de par só enxerga separador literal.
 *
 * Decodifica em RUNS de escapes (`(%XX)+`) e não escape a escape: sequência UTF-8 de vários bytes
 * (`%C3%A9`) precisa ser decodificada junta. O que não casar com a forma `%XX` fica literal.
 *
 * Quando o run inteiro é inválido, cai para octeto a octeto — senão um byte quebrado engoliria o
 * separador colado nele: `%C3%3F` falha como run, e com ele iria embora o `?` que abre a query
 * aninhada, deixando o par seguinte sem separador e, portanto, sem redação. O que continuar
 * inválido (`%C3`) fica literal.
 *
 * Custo aceito, e é só cosmético: num run inválido, uma sequência UTF-8 **válida** do mesmo run
 * também fica literal, e um re-encode posterior a escreve como `%25C3%25A7` — o `ç` de
 * `%C3%A7%FF%3F` deixa de ser legível no Datadog. Medido: a redação continua acontecendo nesse
 * caso (o valor sensível sai `REDACTED`). Separar subsequências válidas exigiria um scanner de
 * fronteira de UTF-8 dentro do caminho que decide o que sai para o Datadog, e a troca não
 * compensa: a entrada já chegou corrompida, e simplicidade aqui vale mais que fidelidade.
 */
function decodificarTolerante(value: string): string {
  return value.replace(/(?:%[0-9A-Fa-f]{2})+/g, (sequencia) => {
    try {
      return decodeURIComponent(sequencia);
    } catch {
      return sequencia.replace(/%[0-9A-Fa-f]{2}/g, (octeto) => {
        try {
          return decodeURIComponent(octeto);
        } catch {
          return octeto;
        }
      });
    }
  });
}

/**
 * Redige o que houver DENTRO do valor de um parâmetro não-sensível — o caso do `returnUrl=` com a
 * query do prefill percent-encoded. Devolve o valor original, byte a byte, quando nada muda: assim
 * um valor inocente nunca é reescrito só por ter passado por aqui (re-encodar normaliza escapes).
 */
function redigirValorAninhado(rawValue: string, profundidade: number): string {
  // Sem `=` (cru ou escapado) não há par aninhado possível — atalho que evita decodificar o valor
  // de todo parâmetro de toda URL de todo evento.
  if (!/=|%3d/i.test(rawValue)) {
    return rawValue;
  }

  const decodificado = decodificarTolerante(rawValue);
  const redigido = redigirNivel(decodificado, profundidade);

  if (redigido === decodificado) {
    return rawValue;
  }

  return decodificado === rawValue ? redigido : encodeURIComponent(redigido);
}

function redigirNivel(value: string, profundidade: number): string {
  return value.replace(
    novoPadraoDePar(profundidade),
    (match, separator: string, name: string, rawValue: string) => {
      if (rawValue.length === 0) {
        return match;
      }

      if (isSensitiveQueryParam(name)) {
        return `${separator}${name}=${REDACTED_QUERY_VALUE}`;
      }

      if (profundidade >= PROFUNDIDADE_MAXIMA) {
        return match;
      }

      const aninhado = redigirValorAninhado(rawValue, profundidade + 1);

      return aninhado === rawValue ? match : `${separator}${name}=${aninhado}`;
    },
  );
}

/**
 * Troca por `REDACTED` o valor de todo parâmetro sensível encontrado em `value`, preservando o
 * resto da string exatamente como veio — inclusive quando o par está aninhado dentro do valor de
 * outro parâmetro. Aceita URL absoluta, relativa ou texto com URL embutida; nunca lança.
 */
export function redactSensitiveQueryParams(value: string): string {
  return redigirNivel(value, 0);
}

/** `#…` logo depois de uma URL absoluta ou de um caminho, até o fim da URL. */
const FRAGMENTO_DE_URL = /((?:[a-z][a-z0-9+.-]*:\/\/|\/)[^\s"'#]*)#[^\s"']*/gi;

/**
 * Tira o fragmento (`#…`) de toda URL do texto. A tela de envio da Arbitragem leva no fragmento o
 * token do link de uso único (`/arbitral/envio#t=…`, 20q D9): o navegador não o manda ao servidor,
 * mas o RUM e o PostHog leem `location.href` inteiro. A tela tira o token da barra e nem sobe a
 * telemetria; cortar aqui é a segunda linha, para qualquer rota.
 */
export function cortarFragmentosDeUrl(value: string): string {
  return value.replace(FRAGMENTO_DE_URL, '$1');
}

/** A redação de uma URL ou de um texto com URL: sem fragmento e sem valor sensível na query. */
function redigirUrl(value: string): string {
  return redactSensitiveQueryParams(cortarFragmentosDeUrl(value));
}

/**
 * `true` quando a redação mudaria o texto — ou seja, quando há valor sensível ali dentro.
 *
 * Aceita `unknown` de propósito: o tipo do SDK diz `string`, mas a causa vem de `Error.cause` de
 * qualquer biblioteca. `message: null` derrubaria o `.replace` — e como o `beforeSend` falha
 * aberto (ver `redactRumEvent`), um throw aqui publicaria o evento.
 */
function contemValorSensivel(text: unknown): boolean {
  return typeof text === 'string' && redigirUrl(text) !== text;
}

/**
 * `beforeSend` do RUM. Redige in place os campos que o SDK aceita alterar (ver a tabela no topo) e
 * devolve `true`, publicando o evento sem o dado pessoal em vez de perder a telemetria.
 *
 * Devolve `false` em dois casos, os dois pela mesma razão — não existe "publicar redigido":
 *
 * 1. o dado pessoal está em `error.causes[]`, que o SDK não copia de volta;
 * 2. algo lançou no meio da redação.
 *
 * O caso 2 importa porque este controle falha **aberto** por padrão: o SDK embrulha o `beforeSend`
 * em `catchUserErrors` (`@datadog/browser-core/src/domain/configuration/configuration.ts:403`), que
 * só faz `display.error` e devolve `undefined` — o evento seguiria publicado com a redação pela
 * metade, sem sinal para quem opera. Num controle cujo propósito é não vazar, o lado seguro do erro
 * é não publicar. `shouldSend` (`.../domain/assembly.ts:133`) descarta o evento com esse `false`.
 *
 * O descarte é **sinalizado uma vez**: antes deste `catch`, uma exceção ao menos virava
 * `display.error` no console via `catchUserErrors`. Engolir isso trocaria um modo de falha visível
 * por outro mudo — se a redação passar a lançar de forma sistêmica (bump do SDK muda a forma do
 * evento, campo deixa de ser `string`), todo evento do RUM some e o único sintoma seria um painel
 * vazio. A trava de módulo existe porque a própria ação de aviso volta a passar por aqui.
 *
 * ⚠️ O aviso é **best-effort, e cobre a falha pontual, não a total**: numa falha sistêmica a
 * própria ação de aviso é montada, reentra no `beforeSend`, lança de novo e é descartada pelo
 * mesmo mecanismo que deveria denunciá-la. Sinal fora do canal do próprio RUM resolveria; não
 * está aqui porque o único disponível no browser é o console, que o repo trata como bug em código
 * commitado.
 */
let falhaDeRedacaoJaSinalizada = false;

function sinalizarFalhaDeRedacao(): void {
  if (falhaDeRedacaoJaSinalizada) {
    return;
  }

  falhaDeRedacaoJaSinalizada = true;

  try {
    datadogRum.addAction('rum_redaction_failed');
  } catch {
    // Sinalizar é best-effort: se nem isso funciona, o descarte continua valendo.
  }
}

export const redactRumEvent: RumBeforeSend = (event: RumEvent) => {
  try {
    return redigirEventoRum(event);
  } catch {
    sinalizarFalhaDeRedacao();
    return false;
  }
};

function redigirEventoRum(event: RumEvent): boolean {
  // `view` existe em todo evento do RUM: é por ele que a URL do navegador (prefill de wizard com
  // CPF, nome e endereço na query) chega ao Datadog, não só nos eventos de view.
  event.view.url = redigirUrl(event.view.url);

  if (event.view.referrer) {
    event.view.referrer = redigirUrl(event.view.referrer);
  }

  if (event.type === 'view') {
    // A URL do elemento de maior pintura é uma URL como outra qualquer — imagem servida por rota
    // que aceite query string entra aqui.
    const lcp = event.view.performance?.lcp;

    if (lcp?.resource_url) {
      lcp.resource_url = redigirUrl(lcp.resource_url);
    }
  }

  if (event.type === 'long_task') {
    event.long_task.scripts?.forEach((script) => {
      if (script.source_url) {
        script.source_url = redigirUrl(script.source_url);
      }

      // `invoker` é a URL do script quando `invoker_type` é `classic-script`/`module-script`;
      // fica no mesmo objeto de `source_url` e vaza pelo mesmo caminho.
      if (script.invoker) {
        script.invoker = redigirUrl(script.invoker);
      }
    });
  }

  if (event.type === 'resource') {
    event.resource.url = redigirUrl(event.resource.url);
  }

  if (event.type === 'error') {
    // Erro de rede traz a URL dentro da mensagem e do stack, não só em `error.resource`.
    event.error.message = redigirUrl(event.error.message);

    if (event.error.stack) {
      event.error.stack = redigirUrl(event.error.stack);
    }

    if (event.error.handling_stack) {
      event.error.handling_stack = redigirUrl(event.error.handling_stack);
    }

    if (event.error.resource) {
      event.error.resource.url = redigirUrl(event.error.resource.url);
    }

    // `error.causes[]` está fora da allowlist do SDK: redigir aqui não chegaria ao evento
    // publicado. O SDK monta `causes` percorrendo a cadeia de `Error.cause`
    // (`@datadog/browser-core/src/domain/error/error.ts:71,113-128`), e o app produz essa cadeia
    // (`shared/utils/documentoPreview.ts:83`), então o caminho é alcançável de verdade.
    const causaVazaria = event.error.causes?.some(
      (cause) => contemValorSensivel(cause.message) || contemValorSensivel(cause.stack),
    );

    if (causaVazaria) {
      return false;
    }
  }

  return true;
}
